import { Copy, LoaderCircle, MonitorUp } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Brand } from "../components/Brand";
import { useAuth } from "../features/auth/AuthContext";
import { savePrejoinTicket } from "../features/room/roomSession";
import { api, assertRoomResolution, getApiErrorMessage } from "../lib/api";
import { talkBrand } from "../lib/brand";
import { EMBED_PARENT_ORIGINS, EMBED_SOURCE, PORTAL_SOURCE, isPortalEmbedMessage, postEmbed } from "../lib/embedBridge";
import { createGuestIdentityDraft, saveGuestIdentityDraft } from "../lib/guestIdentity";
import { isCompleteRoomCode, normalizeRoomCode, roomInviteURL } from "../lib/navigation";

function embedURL(code: string, name: string, gameId: string): string {
  const url = new URL("/embed", talkBrand.origin);
  url.searchParams.set("code", normalizeRoomCode(code));
  if (name.trim()) url.searchParams.set("name", name.trim());
  if (gameId.trim()) url.searchParams.set("game", gameId.trim());
  return url.toString();
}

export function EmbedPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialCode = normalizeRoomCode(params.get("code") ?? "");
  const [code, setCode] = useState(initialCode);
  const [name, setName] = useState(params.get("name") ?? "");
  const [gameId, setGameId] = useState(params.get("game") ?? "");
  const [title, setTitle] = useState(params.get("game") ? `Партия ${params.get("game")}` : "Игровая комната");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [createdCode, setCreatedCode] = useState("");
  const [copied, setCopied] = useState("");

  const snippet = useMemo(() => {
    const activeCode = createdCode || (isCompleteRoomCode(code) ? normalizeRoomCode(code) : "");
    if (!activeCode) return "";
    const src = embedURL(activeCode, name, gameId);
    return `<iframe\n  src="${src}"\n  allow="camera; microphone; display-capture; autoplay; fullscreen"\n  title="Голос и видео партии"\n  style="width:100%;height:100%;border:0"></iframe>`;
  }, [code, createdCode, gameId, name]);

  async function openRoom(nextCode: string, displayName: string, nextGameId: string) {
    const normalized = normalizeRoomCode(nextCode);
    if (!isCompleteRoomCode(normalized)) {
      setError("Нужен полный код комнаты.");
      return;
    }
    setBusy("join");
    setError("");
    try {
      const result = assertRoomResolution(await api.rooms.resolve(normalized));
      const draft = createGuestIdentityDraft();
      saveGuestIdentityDraft(result.room.id, {
        ...draft,
        displayName: displayName.trim() || draft.displayName,
      });
      savePrejoinTicket(result.room.id, result.prejoinTicket);
      postEmbed({
        type: "room",
        gameId: nextGameId,
        roomId: result.room.id,
        code: normalized,
        title: result.room.title,
      });
      navigate(`/r/${encodeURIComponent(result.room.id)}/prejoin`, { state: { room: result.room } });
    } catch (nextError) {
      setError(getApiErrorMessage(nextError));
      postEmbed({ type: "error", gameId: nextGameId, message: getApiErrorMessage(nextError) });
      setBusy("");
    }
  }

  useEffect(() => {
    postEmbed({ type: "ready" });
    const onMessage = (event: MessageEvent) => {
      if (!(EMBED_PARENT_ORIGINS as readonly string[]).includes(event.origin)) return;
      if (!isPortalEmbedMessage(event.data)) return;
      const nextCode = normalizeRoomCode(event.data.code ?? "");
      const nextName = typeof event.data.displayName === "string" ? event.data.displayName : "";
      const nextGame = typeof event.data.gameId === "string" ? event.data.gameId : "";
      setCode(nextCode);
      setName(nextName);
      setGameId(nextGame);
      void openRoom(nextCode, nextName, nextGame);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // The portal can retarget this frame after the first paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auth.loading || !isCompleteRoomCode(initialCode)) return;
    void openRoom(initialCode, params.get("name") ?? "", params.get("game") ?? "");
    // Open a linked game room once, after the account check settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.loading]);

  async function createRoom() {
    if (!auth.snapshot || busy) return;
    setBusy("create");
    setError("");
    try {
      const created = await api.rooms.create({
        title: title.trim() || "Игровая комната",
        allowGuests: true,
        haramModeDefault: false,
        participantLimit: 12,
      });
      setCreatedCode(created.code);
      setCode(created.code);
      postEmbed({ type: "created", gameId, roomId: created.room.id, code: created.code, title: created.room.title });
    } catch (nextError) {
      setError(getApiErrorMessage(nextError));
    } finally {
      setBusy("");
    }
  }

  async function copy(value: string, label: string) {
    await navigator.clipboard.writeText(value);
    setCopied(label);
  }

  const activeCode = createdCode || (isCompleteRoomCode(code) ? normalizeRoomCode(code) : "");

  return (
    <main id="main-content" className="embed-page" tabIndex={-1}>
      <header className="embed-page__bar">
        <Brand />
        <span>Для игровой комнаты</span>
      </header>
      <section className="embed-page__panel" aria-labelledby="embed-title">
        <div className="embed-page__heading">
          <MonitorUp aria-hidden="true" size={28} />
          <div>
            <h1 id="embed-title">Голос и видео внутри партии</h1>
            <p>Портал открывает эту страницу во фрейме игровой комнаты. Камера выключена — на месте игрока шахматная фигура.</p>
          </div>
        </div>

        {busy === "join" ? <p className="embed-page__status" role="status"><LoaderCircle className="spin" aria-hidden="true" size={18} />Открываем комнату…</p> : null}

        <form className="form-stack" onSubmit={(event) => { event.preventDefault(); void openRoom(code, name, gameId); }}>
          <label className="field"><span>Код комнаты</span><input value={code} autoComplete="off" spellCheck={false} onChange={(event) => setCode(event.target.value.toUpperCase())} placeholder="ABCD-EFGH-JKLM" /></label>
          <label className="field"><span>Имя в партии</span><input value={name} maxLength={64} autoComplete="name" onChange={(event) => setName(event.target.value)} placeholder="Белые" /></label>
          <label className="field"><span>Номер партии на портале</span><input value={gameId} maxLength={80} onChange={(event) => setGameId(event.target.value)} placeholder="game-42" /></label>
          <button className="button button--primary button--wide" type="submit" disabled={Boolean(busy) || !isCompleteRoomCode(normalizeRoomCode(code))}>Войти в комнату</button>
        </form>

        {auth.snapshot ? (
          <form className="form-stack embed-page__create" onSubmit={(event) => { event.preventDefault(); void createRoom(); }}>
            <label className="field"><span>Новая комната для партии</span><input value={title} maxLength={100} onChange={(event) => setTitle(event.target.value)} /></label>
            <button className="button button--secondary button--wide" type="submit" disabled={Boolean(busy) || !title.trim()}>{busy === "create" ? "Создаём…" : "Создать комнату"}</button>
          </form>
        ) : <p className="embed-page__note">Создать комнату может только вошедший аккаунт. Соперник заходит по коду без регистрации.</p>}

        {error ? <p className="form-error" role="alert">{error}</p> : null}

        {activeCode && snippet ? (
          <div className="embed-page__snippet">
            <div className="embed-page__snippet-actions">
              <button type="button" className="button button--secondary button--small" onClick={() => void copy(embedURL(activeCode, name, gameId), "link")}><Copy aria-hidden="true" size={16} />{copied === "link" ? "Ссылка скопирована" : "Ссылка для фрейма"}</button>
              <button type="button" className="button button--secondary button--small" onClick={() => void copy(snippet, "iframe")}><Copy aria-hidden="true" size={16} />{copied === "iframe" ? "Код скопирован" : "Код iframe"}</button>
              <button type="button" className="button button--primary button--small" onClick={() => void openRoom(activeCode, name, gameId)}>Открыть</button>
            </div>
            <pre>{snippet}</pre>
            <p>Обычная ссылка для браузера: {roomInviteURL(activeCode)}</p>
          </div>
        ) : null}

        <div className="embed-page__contract">
          <h2>Как портал управляет комнатой</h2>
          <p>Родительская страница шлёт сообщение во фрейм. Ответ приходит с <code>{EMBED_SOURCE}</code>.</p>
          <pre>{`iframe.contentWindow.postMessage({
  source: "${PORTAL_SOURCE}",
  type: "join",
  code: "ABCD-EFGH-JKLM",
  displayName: "Белые",
  gameId: "game-42"
}, "${talkBrand.origin}");`}</pre>
          <p>Разрешённые родители: {EMBED_PARENT_ORIGINS.join(", ")}. У iframe нужен атрибут <code>allow="camera; microphone; display-capture; autoplay"</code>.</p>
        </div>
      </section>
    </main>
  );
}
